import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { Select } from "../../../components/Select";
import Modal from "../../../components/Modal/Modal";
import Titulo from "../../../components/Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import { AssinaturaApi } from "../../../hooks/assinaturaApi";
import { DadosCartao } from "../../../types/Assinatura";
import { ViewModalPagamento } from "../styles";

interface ModalAtualizarCartaoProps {
  isOpen: boolean;
  setOpen: (v: boolean) => void;
  onSucesso: () => void;
  /**
   * Quando há fatura aberta, atualizar o cartão dispara a cobrança — passamos
   * o valor pra mostrar "Pagamento de R$ X". Sem valor = só atualiza o cartão
   * (o backend não cobra quando não há ciclo aberto).
   */
  valorCobranca?: number;
  /** Máximo de parcelas do plano. Se > 1 e há cobrança, mostra o seletor. */
  maxParcelas?: number;
}

/**
 * Modal de atualização de cartão, reaproveitado pela seção de fatura aberta
 * (PagarFatura) e pelo botão avulso "Atualizar cartão" da /assinatura.
 *
 * O endpoint atualizarCartao decide sozinho: com ciclo aberto cobra (result.cobrou),
 * sem ciclo aberto só salva o cartão (result.cobrou=false).
 */
export const ModalAtualizarCartao: React.FC<ModalAtualizarCartaoProps> = ({
  isOpen,
  setOpen,
  onSucesso,
  valorCobranca,
  maxParcelas,
}) => {
  const { theme } = useTheme();

  const [cartao, setCartao] = useState<DadosCartao>({
    holder_name: "",
    number: "",
    exp_month: 0,
    exp_year: 0,
    cvv: "",
  });
  const [validadeStr, setValidadeStr] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [parcelas, setParcelas] = useState(1);

  const cobra = valorCobranca != null;
  const maxP = maxParcelas && maxParcelas > 0 ? maxParcelas : 1;
  const mostrarParcelas = cobra && maxP > 1;

  const formatarValor = (v: number) =>
    v.toLocaleString("pt-BR", { minimumFractionDigits: 2 });

  // Reseta o form ao abrir.
  useEffect(() => {
    if (isOpen) {
      setCartao({ holder_name: "", number: "", exp_month: 0, exp_year: 0, cvv: "" });
      setValidadeStr("");
      setParcelas(1);
    }
  }, [isOpen]);

  const handleValidadeChange = (v: string) => {
    const limpo = v.replace(/\D/g, "").slice(0, 4);
    const formatado =
      limpo.length > 2 ? `${limpo.slice(0, 2)}/${limpo.slice(2)}` : limpo;
    setValidadeStr(formatado);

    if (limpo.length >= 2) {
      const mes = parseInt(limpo.slice(0, 2), 10);
      setCartao((c) => ({ ...c, exp_month: mes }));
    }
    if (limpo.length === 4) {
      const ano = 2000 + parseInt(limpo.slice(2, 4), 10);
      setCartao((c) => ({ ...c, exp_year: ano }));
    }
  };

  const handleSubmit = async () => {
    if (
      !cartao.holder_name ||
      !cartao.number ||
      !cartao.exp_month ||
      !cartao.exp_year ||
      !cartao.cvv
    ) {
      Swal.fire({ icon: "warning", title: "Preencha todos os campos", timer: 2000 });
      return;
    }

    setEnviando(true);
    try {
      const result = await AssinaturaApi().atualizarCartao(cartao, mostrarParcelas ? parcelas : undefined);

      if (result.ok && result.cobrou && result.aprovado) {
        setOpen(false);
        await Swal.fire({ icon: "success", title: "Pagamento aprovado!", text: result.mensagem });
        window.location.reload();
        return;
      } else if (result.ok && !result.cobrou) {
        setOpen(false);
        await Swal.fire({ icon: "success", title: result.mensagem });
        onSucesso();
      } else {
        Swal.fire({
          icon: "error",
          title: cobra ? "Pagamento não autorizado" : "Não foi possível salvar o cartão",
          text: result.mensagem,
        });
      }
    } catch (err: any) {
      console.error("Erro ao atualizar cartão:", err);
      Swal.fire({
        icon: "error",
        title: "Erro ao processar",
        text:
          err?.response?.data?.erro ||
          "Não foi possível processar. Tente novamente.",
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setModal={setOpen}
      width={"70rem"}
      align="center"
      justify="center"
      animation="opacity 400ms cubic-bezier(0.4, 0, 0.2, 1)"
      margin="0rem 2rem"
      padding={"3rem"}
    >
      <Titulo
        justifyContent="center"
        flexDirection="column-reverse"
        titulo="Atualizar cartão"
        subtitulo={
          cobra
            ? `Os novos dados serão usados para as próximas cobranças. Pagamento da fatura atual de R$${formatarValor(valorCobranca as number)}`
            : "Os novos dados serão usados na próxima cobrança."
        }
      />
      <ViewModalPagamento theme={theme}>
        <div className="form-cartao">
          <Input
            type="text"
            label="Nome impresso no cartão"
            value={cartao.holder_name}
            onChange={(v) =>
              setCartao((c) => ({ ...c, holder_name: v.toUpperCase() }))
            }
            placeholder="Nome completo"
            ignoreValidations
          />
          <Input
            type="text"
            label="Número do cartão"
            value={cartao.number}
            onChange={(v) =>
              setCartao((c) => ({ ...c, number: v.replace(/\D/g, "") }))
            }
            placeholder="0000 0000 0000 0000"
            maxLength={19}
            showMaxLength={false}
            ignoreValidations
          />
          <div className="linha-dupla">
            <Input
              type="text"
              label="Validade (MM/AA)"
              value={validadeStr}
              onChange={(v) => handleValidadeChange(v)}
              placeholder="MM/AA"
              maxLength={5}
              showMaxLength={false}
              ignoreValidations
            />
            <Input
              type="text"
              label="CVV"
              value={cartao.cvv}
              onChange={(v) =>
                setCartao((c) => ({ ...c, cvv: v.replace(/\D/g, "") }))
              }
              placeholder="123"
              maxLength={4}
              showMaxLength={false}
              ignoreValidations
            />
          </div>
          {mostrarParcelas && (
            <Select
              label="Parcelas"
              value={parcelas}
              onChange={(v) => setParcelas(Number(v))}
              options={Array.from({ length: maxP }, (_, i) => i + 1).map((n) => ({
                name: `${n}x de R$ ${formatarValor((valorCobranca as number) / n)}`,
                value: n,
              }))}
            />
          )}
        </div>
        <div className="acoes-modal">
          <Button
            title="Cancelar"
            className="button cinza fonteTexto"
            onClick={() => setOpen(false)}
            width="auto"
            padding="0 2rem"
          />
          <Button
            title={enviando ? "Processando..." : cobra ? "Confirmar pagamento" : "Salvar cartão"}
            className="fonteTexto"
            onClick={handleSubmit}
            width="auto"
            padding="0 2rem"
            loading={enviando}
          />
        </div>
      </ViewModalPagamento>
    </Modal>
  );
};

export default ModalAtualizarCartao;
